import { FabricCuttingShapeEngine } from '@/infrastructure/engine2d/cuttingShape/FabricCuttingShapeEngine';
import { FabricEngine2D } from '@/infrastructure/engine2d/FabricEngine2D';
import { FabricExporter2D } from '@/infrastructure/engine2d/FabricExporter2D';
import { FabricPortEventsService } from '@/infrastructure/engine2d/events/FabricPortEventsService';
import { FabricPrintZoneEngine2D } from '@/infrastructure/engine2d/FabricPrintZoneEngine2D';
import { FabricScene } from '@/infrastructure/engine2d/FabricScene';
import { FabricViewportEngine2D } from '@/infrastructure/engine2d/FabricViewportEngine2D';
import type { IEngine2DInitParams } from '@/application/ports/IEngine2DInitParams';
import type { TSide } from '@/domain/shared/TSide';

// Composition root for the Fabric 2D adapters. One FabricScene per
// side, shared by every adapter so they operate on the same canvas.
// The shared FabricPortEventsService is passed to both the entity
// engine and the viewport engine so zoom / after-render / entity
// events all flow through a single bus.
//
// Vue-side callers never see the scene directly — they receive the
// bound port methods via useEditor2D. Infrastructure consumers that
// need the raw scene (3D bridge, print export, test hooks) go through
// getFabricSceneForTests.

const _scenesBySide = new Map<TSide, FabricScene>();

function createFabricAdapters(
    side: TSide,
    canvasElement: HTMLCanvasElement,
    initParams: IEngine2DInitParams,
): {
    cuttingShapeEngine: FabricCuttingShapeEngine;
    dispose: () => void;
    engine: FabricEngine2D;
    exporter: FabricExporter2D;
    printZoneEngine: FabricPrintZoneEngine2D;
    viewportEngine: FabricViewportEngine2D;
} {
    const scene = new FabricScene(canvasElement, initParams);
    _scenesBySide.set(side, scene);

    const events = new FabricPortEventsService({ canvas: scene.fabricCanvas, scene });
    const engine = new FabricEngine2D({ events, scene });
    const viewportEngine = new FabricViewportEngine2D({ events, scene });
    const printZoneEngine = new FabricPrintZoneEngine2D({ engine, scene });
    const exporter = new FabricExporter2D({ scene });
    const cuttingShapeEngine = new FabricCuttingShapeEngine(scene);

    return {
        cuttingShapeEngine,
        engine,
        exporter,
        printZoneEngine,
        viewportEngine,
        dispose: () => {
            printZoneEngine.dispose();
            viewportEngine.dispose();
            engine.dispose();
            events.dispose();
            scene.dispose();
            _scenesBySide.delete(side);
        },
    };
}

function getFabricSceneForTests(side: TSide): FabricScene | undefined {
    return _scenesBySide.get(side);
}

export { createFabricAdapters, getFabricSceneForTests };
